'use client';

import { type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { SpatialMascotState } from './SpatialMascot';
import { WorkspaceMetric, type WorkspaceHeroProps } from './WorkspaceHero';
import { WorkspaceShell, type SpatialDeckSection } from './WorkspaceShell';
import type { PageToolbarProps } from './PageToolbar';
import { Modal, ModalBody } from './Modal';
import { SettingsPresentationContext } from './settingsPresentation';

// The hero metric lives in its own module so the control deck can mount it without an import cycle back
// through this one. The plugin UI runtime takes WorkspaceMetric from this compatibility path.
export { WorkspaceMetric };

export function WorkspacePage({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`workspace-page ${className}`}>{children}</div>;
}

export interface SpatialWorkspaceLayoutProps {
  hero: Pick<WorkspaceHeroProps, 'eyebrow' | 'title' | 'count' | 'description' | 'status' | 'action'> & { mascotState?: SpatialMascotState; metrics: ReactNode };
  navigation?: {
    sections: SpatialDeckSection[];
    value: string;
    onChange: (id: string) => void;
    ariaLabel: string;
  };
  toolbar?: PageToolbarProps;
  children: ReactNode;
  className?: string;
}

/** The register shell, kept under its pre-unification name: eleven plugin bundles across two
 *  repositories mount it, and the plugin API version is a compatibility ceiling that cannot express a
 *  removal. A thin alias onto WorkspaceShell's `register` variant. */
export function SpatialWorkspaceLayout({ hero, navigation, toolbar, children, className = '' }: SpatialWorkspaceLayoutProps) {
  const { metrics, mascotState = 'idle', ...heroProps } = hero;
  return (
    <WorkspaceShell variant="register" className={className} hero={{ ...heroProps, mascot: mascotState, metrics }} navigation={navigation} toolbar={toolbar}>
      {children}
    </WorkspaceShell>
  );
}

/** The master/detail rail: a surface you open to READ a record, not to work in one.
 *
 *  It is the shared `Modal` with the rail's width. It used to be a second dialog
 *  implementation — its own portal, its own overlay-stack registration, its own Radix `DialogContent`,
 *  its own backdrop, header, close control and scroll body, and its own paint and geometry in
 *  `app/styles/components/workspace-detail.css`. Two implementations of one thing is two of everything
 *  that can be got wrong, and they had already diverged: the rail dismissed on `mousedown` where the
 *  dialog required a press that BEGAN on the backdrop (so releasing a drag from inside the rail onto the
 *  backdrop closed it), and it painted itself `--color-document` with a hand-rolled cast shadow where
 *  the dialog took the shared raised material.
 *
 *  Everything the name promised survives the collapse, because `Modal` already owned all of it: the
 *  portal to <body>, the overlay stack's `inert` isolation and scroll lock, the focus it puts in and
 *  gives back, the `OverlayDepthProvider` that makes anything opened from inside the rail resolve one
 *  level deeper, and the full-screen presentation on a phone — where a side rail would leave a useless
 *  strip of backdrop beside it.
 *
 *  Depth and viewport resolve the shape, which owns the z-band.
 *  `drawerWidth="default"` is the rail's own width
 *  (`min(38rem, calc(100vw - 3rem))`), stated because `Modal` widens a drawer for `size="lg"`.
 *
 *  `sectioned` is the record drawer whose body is Settings content (the Project and User drawers, and the
 *  plugin panels mounted in them): it draws every Settings primitive in the body in the compact drawer
 *  presentation (`SettingsPresentationContext` in `./settingsPresentation`), sections with a title over one
 *  hairline-framed box without internal dividers instead of raised cards. Without it the body is whatever the caller draws.
 *
 *  Kept as a named component rather than folded into call sites: eleven plugin bundles across two
 *  repositories mount it through the plugin UI runtime, and the plugin API version is a compatibility
 *  ceiling that cannot express a removal. */
export function WorkspaceDetailRail({ label, description, icon, iconNode, actions, titleAside, tabs, footer, closeLabel, onClose, closeDisabled = false, 'aria-busy': busy, reportedBy, scrim = 'default', sectioned = false, children }: {
  label: string;
  /** The meta line under the record's name — a path, an id, a source, or `DetailMeta` for a status dot and a
   *  kind. It belongs in the header `Modal` already draws; written into the body instead it becomes a
   *  SECOND header band stacked under the first, which is what every rail here had grown. */
  description?: ReactNode;
  /** The icon tile in front of the title. */
  icon?: LucideIcon;
  /** A pre-rendered brand mark in the same tile instead of `icon`. */
  iconNode?: ReactNode;
  /** Header actions on the title row, before the close control (an Edit button, an `ActionMenu`). */
  actions?: ReactNode;
  /** A mark of the record's name itself, drawn beside it on the title's line: what the surface is and how
   *  it behaves, read on demand. Something that ACTS on the record is an `action`. */
  titleAside?: ReactNode;
  /** A tab strip (a `line` Segmented) inside the header, above its single hairline. */
  tabs?: ReactNode;
  /** Pinned below the body; pass a `ModalFooter` and only when the rail edits. */
  footer?: ReactNode;
  closeLabel: string;
  onClose: () => void;
  /** Keep more of the source visualization visible behind an inspection drawer. */
  scrim?: 'default' | 'soft';
  /** Prevent dismissal while an editor inside the rail has an in-flight save. */
  closeDisabled?: boolean;
  /** Preserve an editor's busy announcement when it uses the sectioned record-drawer seam. */
  'aria-busy'?: true;
  /** The `owner` of a writer that belongs to the page and is edited in this rail (`Modal.reportedBy`): its saved
   *  and recovery cards are raised in the rail while it is open. */
  reportedBy?: string;
  /** The body is Settings sections: its `SettingsGroup`, `SettingsRow`, `SettingsValue`, `SettingsList`,
   *  `SettingsItem` and `SettingsState` draw in the drawer presentation rather than as Settings cards. */
  sectioned?: boolean;
  children: ReactNode;
}) {
  return (
    <Modal title={label} description={description} icon={icon} iconNode={iconNode} headerActions={actions} titleAside={titleAside} tabs={tabs} closeLabel={closeLabel} onClose={onClose} closeDisabled={closeDisabled} aria-busy={busy} reportedBy={reportedBy} scrim={scrim} size="md" drawerWidth="default">
      <ModalBody gap={7}>{sectioned ? <SettingsPresentationContext value="drawer">{children}</SettingsPresentationContext> : children}</ModalBody>
      {footer}
    </Modal>
  );
}
